<script setup lang="ts">
import { ref, onMounted, onUnmounted, type Ref } from 'vue'
import createGlobe, { type COBEOptions } from 'cobe'
import { cn } from '@/lib/utils'

interface Props {
  className?: string
  config?: COBEOptions
}

const props = withDefaults(defineProps<Props>(), {
  className: '',
  config: () => ({
    width: 800,
    height: 800,
    onRender: () => {},
    devicePixelRatio: 2,
    phi: 0,
    theta: 0.3,
    dark: 0,
    diffuse: 0.4,
    mapSamples: 16000,
    mapBrightness: 1.2,
    baseColor: [1, 1, 1],
    markerColor: [0.517, 0.212, 0.710],
    glowColor: [0.3, 0.3, 0.3],
    markers: [
      { location: [14.5995, 120.9842], size: 0.03 },
      { location: [19.076, 72.8777], size: 0.1 },
      { location: [23.8103, 90.4125], size: 0.05 },
      { location: [30.0444, 31.2357], size: 0.07 },
      { location: [39.9042, 116.4074], size: 0.08 },
      { location: [-23.5505, -46.6333], size: 0.1 },
      { location: [19.4326, -99.1332], size: 0.1 },
      { location: [40.7128, -74.006], size: 0.1 },
      { location: [34.6937, 135.5022], size: 0.05 },
      { location: [41.0082, 28.9784], size: 0.06 },
    ],
  })
})

// Refs
const canvasRef: Ref<HTMLCanvasElement | null> = ref(null)

let phi = 0
let width = 0
let globe: any = null

onMounted(() => {
  if (!canvasRef.value) return

  const onResize = () => {
    if (canvasRef.value) {
      width = canvasRef.value.offsetWidth
    }
  }

  window.addEventListener('resize', onResize)
  onResize()

  globe = createGlobe(canvasRef.value, {
    ...props.config,
    width: width * 2,
    height: width * 2,
    onRender: (state) => {
      phi += 0.005
      state.phi = phi
      state.width = width * 2
      state.height = width * 2
    },
  })

  setTimeout(() => {
    if (canvasRef.value) {
      canvasRef.value.style.opacity = '1'
    }
  }, 0)

  onUnmounted(() => {
    if (globe) {
      globe.destroy()
    }
    window.removeEventListener('resize', onResize)
  })
})
</script>

<template>
  <div
    :class="cn(
      'absolute inset-0 mx-auto aspect-[1/1] w-full max-w-[600px]',
      className
    )"
  >
    <canvas
      ref="canvasRef"
      :class="cn(
        'size-full opacity-0 transition-opacity duration-500 [contain:layout_paint_size]'
      )"
    />
  </div>
</template>
